import React from "react";
import {
  Box,
  Typography,
  Stack,
  useTheme,
  alpha,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";

const PageHeader = ({
  title,
  subtitle,
  icon: Icon,
  primaryAction,
  secondaryActions,
  breadcrumbs,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        mb: 3,
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        background: isDark
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1)}`,
        backdropFilter: "blur(10px)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || theme.palette.primary.light})`,
        },
      }}
    >
      {breadcrumbs && (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 1.5 }}
        >
          {breadcrumbs.map((crumb, i) => (
            <Link
              key={i}
              underline="hover"
              color={
                i === breadcrumbs.length - 1 ? "text.primary" : "text.secondary"
              }
              sx={{
                fontSize: "0.75rem",
                fontWeight: i === breadcrumbs.length - 1 ? 600 : 400,
              }}
            >
              {crumb}
            </Link>
          ))}
        </Breadcrumbs>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {Icon && (
            <Box
              sx={{
                width: { xs: 44, sm: 52 },
                height: { xs: 44, sm: 52 },
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: { xs: 22, sm: 26 }, color: "#fff" }} />
            </Box>
          )}
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.4rem" },
                color: theme.palette.text.primary,
                letterSpacing: "-0.02em",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.3, fontSize: "0.8125rem" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
        >
          {secondaryActions}
          {primaryAction}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PageHeader;
